import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../../lib/axios";
import { usePermissions } from "../../../../GlobalFunction/userPermissions";
import AddIcon from '@mui/icons-material/Add';
import {
    Box,
    Button,
    Tooltip,
    Breadcrumbs,
    Link,
    Paper,
    Typography,
    Chip,
    CircularProgress
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import HomeIcon from '@mui/icons-material/Home';


export default function HsnCdMstTable() {
    const [isLoading, setIsLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [gridApi, setGridApi] = useState(null);
    const navigate = useNavigate();
    const [tappedRowId, setTappedRowId] = useState(null);
    const [lastTap, setLastTap] = useState(0);
    const DOUBLE_TAP_DELAY = 300;
    const isMobile = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    const permissions = usePermissions("HSN/SAC Code");
    const { AddRec = "0" } = permissions || {};

    const gridStyles = `
  .ag-theme-alpine {
    --ag-border-color: #c8c8c8;
    --ag-header-background-color: #f8f9fa;
    --ag-odd-row-background-color: #fcfcfc;
    --ag-border-radius: 6px;
  }
  
    .ag-theme-alpine .ag-cell {
  padding: 4px 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
  
  .ag-theme-alpine .ag-header-cell {
    border-right: 1px solid #c8c8c8;
    border-bottom: 1px solid #c8c8c8;
    font-weight: 600;
  }
  
  .ag-theme-alpine .ag-floating-filter-input {
    border-radius: 4px;
  }
  
  .ag-theme-alpine .ag-floating-filter-input:focus {
    outline: none;
    border-color: #3f51b5;
    box-shadow: 0 0 0 2px rgba(63, 81, 181, 0.2);
  }
  
  .ag-theme-alpine .ag-header-cell::after {
    display: none;
  }
  
  .ag-theme-alpine .ag-ltr .ag-cell-first-right-pinned {
    border-left: 1px solid #c8c8c8;
  }
  
  .ag-floating-filter-button{
    margin-left: 0px !important 
  }
  
  .ag-header-cell{
    padding-left: 2px;
    padding-right: 0px;
  }
  
  .truncated-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  
  .status-chip {
    height: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }
  
   .ag-input-wrapper:before {
    background-color: white;
    display:none
  }
    .ag-theme-alpine .ag-floating-filter input[type="date"]{
      padding-left: 0 !important;
    }
      :where(.ag-ltr) .ag-header-cell-resize {
    right: -6px !important;
}


@media (max-width: 600px) {
  /* Parent container: column layout, center all items */
  .ag-theme-alpine .ag-paging-panel {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important; /* center children horizontally */
    gap: 12px !important;
    padding: 12px !important;
    min-height: 100px;
    box-sizing: border-box;
    text-align: center;
  }

  /* Wrapper flex container for Total Records + Page Size */
  .ag-theme-alpine .ag-paging-row-summary-panel,
  .ag-theme-alpine .ag-paging-page-size {
    display: inline-flex !important;
    align-items: center;
    gap: 16px;
    font-size: 14px;
    white-space: nowrap; /* prevent wrapping */
    justify-content: center;
    margin: 0 !important; /* remove any margin */
  }

  /* Put Total Records and Page Size side by side, inside a single flex row */
  .ag-theme-alpine .ag-paging-panel > .ag-paging-row-summary-panel,
  .ag-theme-alpine .ag-paging-panel > .ag-paging-page-size {
    flex: none;
  }

  /* Container for the above two so they stay in one row and centered */
  .ag-theme-alpine .ag-paging-panel > .ag-paging-row-summary-panel,
  .ag-theme-alpine .ag-paging-panel > .ag-paging-page-size {
    display: inline-flex !important;
  }

  /* Buttons below, centered */
  .ag-theme-alpine .ag-paging-button-panel {
    width: 100%;
    display: flex !important;
    justify-content: center !important;
    flex-wrap: wrap;
    margin-top: 8px;
  }

  /* Page size dropdown/input styling */
  .ag-theme-alpine .ag-paging-page-size select,
  .ag-theme-alpine .ag-paging-page-size input {
    max-width: 70px !important;
    font-size: 14px !important;
    padding: 4px;
  }
}
`;
    const handleCellClicked = useCallback((params) => {
        if (params.column.isHeader || !params.data) return;

        const hsnCode_Key = params.data.HsnCode_Key;
        if (!hsnCode_Key) return;

        setTappedRowId(hsnCode_Key);
        setTimeout(() => setTappedRowId(null), 300);

        const currentTime = Date.now();
        const timeSince = currentTime - lastTap;

        if (isMobile()) {
            navigate('/hsn-code', {
                state: { HsnCode_Key: hsnCode_Key, mode: 'view' }
            });
            return;
        }

        if (timeSince > 0 && timeSince < DOUBLE_TAP_DELAY) {
            navigate('/hsn-code', {
                state: { HsnCode_Key: hsnCode_Key, mode: 'view' }
            });
        }
        setLastTap(currentTime);
    }, [navigate, lastTap]);

    const getRowStyle = (params) => {
        if (tappedRowId === params.data.HsnCode_Key) {
            return { background: '#bbdefb', transition: 'background 0.2s' };
        }
        return null;
    };
    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.innerHTML = gridStyles;
        document.head.appendChild(styleElement);
        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    useEffect(() => {
        fetchHsnData();
    }, []);

    const fetchHsnData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.post(
                "HsnCode/GetHsnCodeDashBoard",
                {
                    start: 1,
                    PageSize: 1,
                    SearchText: "",
                }
            );
            if (response.data.Status === 0) {
                const formattedData = response.data.Data.map((hsnCode) => ({
                    ...hsnCode,
                    RowNum: hsnCode.RowNum,
                    HsnCode_Key: hsnCode.HsnCode_Key,
                    HsnChap_Name: hsnCode.HsnChap_Name || "",
                    HsnSubChap_Name: hsnCode.HsnSubChap_Name,
                    Hsn_Code: hsnCode.Hsn_Code,
                    HsnCode_Desc: hsnCode.HsnCode_Desc,
                    Remk: hsnCode.Remk,
                    Status:
                        hsnCode.Status === null
                            ? ""
                            : hsnCode.Status == "1"
                                ? "Active"
                                : "Inactive",
                }));
                setRows(formattedData);
                if (gridApi) {
                    setTimeout(() => {
                        gridApi?.sizeColumnsToFit();
                    }, 0);
                }
            } else {
                console.error(
                    "Error fetching hsncode data:",
                    response.data.Message
                );
            }
        } catch (error) {
            console.error("Error fetching hsncode data:", error);
        }
        finally {
            setIsLoading(false);
        }
    };
    // New function to handle click based on permission
    const handlePermittedClick = () => {
        if (AddRec === "1") {
            handleClick();
        }
    };

    const fetchNextHsnCodeKey = async () => {
        try {
            const res = await axiosInstance.post('/HsnCode/GetSeriesLastNewKey', {
                ModuleName: "HSNCODE",
                TblName: "HSNCODE",
                ColName: "HSNCODE_KEY",
                Ncollen: 5,
                CPrefix: "GH",
                FLAG: ""
            });

            if (res.data.Status === 0 && res.data.Data?.[0]?.DocKey) {
                return res.data.Data[0].DocKey;
            }
            throw new Error("Invalid response");
        } catch (err) {
            console.error("Failed to generate HSN Code Key");
            return "GH001";
        }
    };

    const handleClick = async () => {
        const nextKey = await fetchNextHsnCodeKey();
        navigate("/hsn-code", {
            state: {
                mode: "add",
                HsnCode_Key: nextKey
            }
        });
    };

    // const handleClick = () => {
    //     navigate("/hsn-code", { state: { mode: "add" } });
    // };

    const handleHomeClick = () => {
        navigate("/dashboard");
    };

    const handleRowDoubleClick = useCallback((params) => {
        const hsnCode_Key = params?.data?.HsnCode_Key;
        if (hsnCode_Key) {
            navigate('/hsn-code', { state: { HsnCode_Key: hsnCode_Key, mode: 'view' } });
        }
    }, [navigate]);
    const handleLocationclick = () => {
        navigate("/masters/gst-code");
    };
    const onGridReady = (params) => {
        setGridApi(params.api);

        // Resize to fit columns only on large screens
        if (window.innerWidth > 768) {
            setTimeout(() => {
                params.api.sizeColumnsToFit();
            }, 0);
        }
    };
    const addButtonStyles = {
        background: AddRec === "1" ? "#635bff" : "rgba(0, 0, 0, 0.12)",
        color: AddRec === "1" ? "white" : "rgba(0, 0, 0, 0.26)",
        borderRadius: "50px",
        padding: "8px 20px",
        boxShadow: AddRec === "1" ? "0px 6px 15px rgba(0, 0, 0, 0.3)" : "none",
        transition: "background 0.3s ease",
        "&:hover": {
            background: AddRec === "1" ? "#524ccc" : "rgba(0, 0, 0, 0.12)"
        },
        "&:disabled": {
            backgroundColor: "rgba(0, 0, 0, 0.12)",
            color: "rgba(0, 0, 0, 0.26)"
        }
    };

    const TruncatedCellRenderer = (props) => {
        const [isOverflowed, setIsOverflowed] = useState(false);
        const cellRef = React.useRef(null);

        useEffect(() => {
            if (cellRef.current) {
                const isTextOverflowed =
                    cellRef.current.scrollWidth > cellRef.current.clientWidth ||
                    (props.value && props.value.length > 20);
                setIsOverflowed(isTextOverflowed);
            }
        }, [props.value]);

        const cellValue = props.valueFormatted ? props.valueFormatted : props.value;

        return (
            <Tooltip
                title={isOverflowed ? cellValue : ''}
                placement="top"
                arrow
                disableHoverListener={!isOverflowed}
            >
                <div
                    ref={cellRef}
                    className="truncated-cell"
                    style={{ width: '100%' }}
                >
                    {cellValue}
                </div>
            </Tooltip>
        );
    };

    const StatusCellRenderer = (props) => {
        const getStatusColor = (status) => {
            switch (status) {
                case 'Active':
                    return '#4caf50';
                case 'Inactive':
                    return '#f44336';
            }
        };

        return (
            <Chip
                label={props.value}
                className="status-chip"
                sx={{
                    backgroundColor: getStatusColor(props.value),
                    color: 'white',
                    width: '100%',
                    maxWidth: '100px',
                }}
            />
        );
    };
    const columnDefs = [
        { headerName: 'Sr.No.', field: 'RowNum', width: 100, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
        { headerName: 'HsnCode_Key', field: 'HsnCode_Key', minWidth: 200, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
        {
            headerName: 'ChapterName',
            minWidth: 150,
            flex: 1,
            filter: 'agTextColumnFilter',
            cellRenderer: TruncatedCellRenderer,
            valueGetter: (params) => {
                const name = params.data.HsnChap_Name || '';
                const desc = params.data.HsnChap_Desc || '';
                return desc ? `${name} - ${desc}` : name;
            },
            getQuickFilterText: (params) => {
                return `${params.data.HsnChap_Name || ''} ${params.data.HsnChap_Desc || ''}`;
            }
        },
       // { headerName: 'ChapterName', field: 'HsnChap_Name', minWidth: 200, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
      //  { headerName: 'SubChapterName', field: 'HsnSubChap_Name', minWidth: 200, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
         {
            headerName: 'Sub-Chap Name',
            minWidth: 150,
            flex: 1,
            filter: 'agTextColumnFilter',
            cellRenderer: TruncatedCellRenderer,
            valueGetter: (params) => {
                const name = params.data.HsnSubChap_Name || '';
                const desc = params.data.HsnSubChap_Desc || '';
                return desc ? `${name} - ${desc}` : name;
            },
            getQuickFilterText: (params) => {
                return `${params.data.HsnChap_Name || ''} ${params.data.HsnChap_Desc || ''}`;
            }
        }, 
        { headerName: 'HsnCodeName', field: 'Hsn_Code', minWidth: 200, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
        { headerName: 'Remark', field: 'Remk', minWidth: 200, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
        { headerName: 'Status', field: 'Status', width: 140, filter: 'agTextColumnFilter', cellRenderer: StatusCellRenderer },
    ];
    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        filterParams: {
            suppressAndOrCondition: true,
        },
        cellStyle: {
            borderRight: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center'
        },
        headerClass: 'ag-header-cell',
        suppressSizeToFit: true,
    }), []);
    return (
        <Box sx={{
            width: { xs: "calc(100vw - 60px)", sm: "100%", md: "100%" },
            maxWidth: { xs: "calc(100vw - 60px)", sm: "100%", md: "100%" },
            height: '97%', display: 'flex', flexDirection: 'column', p: 2, background: 'rgb(249, 249, 249)'
        }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                flexWrap: 'wrap',
                rowGap: 2,
                background: 'rgb(255, 255, 255)',
                padding: '12px',
                borderRadius: '5px',
                boxShadow: 'rgb(0 0 0 / 8%)'
            }}>
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{
                        fontSize: { xs: "18px", sm: "16px" },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "column", md: "row" }
                    }}
                >
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                        onClick={handleHomeClick}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        <Box component="span">Home</Box>
                    </Link>

                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500" }}
                        onClick={handleLocationclick}
                    >
                        GST/SAC Code
                    </Link>
                    <Typography color="text.primary" sx={{ fontWeight: "500", fontSize: { xs: "18px", sm: "16px" }, }}>  HSN Code Master</Typography>
                </Breadcrumbs>

                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                }}>
                    <Tooltip title={AddRec !== '1' ? 'You have no permission to add record' : 'Add New Record'} placement="top" arrow>
                        <span>
                            <Button
                                variant="contained"
                                onClick={handlePermittedClick}
                                size="small"
                                disabled={AddRec !== '1'}
                                sx={addButtonStyles}
                            >
                                <AddIcon /> New
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            <Paper
                elevation={2}
                sx={{
                    // height: 'calc(100vh - 250px)',
                    height: 'auto', minHeight: 'calc(100vh - 250px)',
                    width: '100%',
                    flexGrow: 1,
                    overflow: 'hidden'
                }}
            >
                <Box
                    className="ag-theme-alpine"
                    style={{
                        height: '100%',
                        width: '100%',
                        overflowX: 'auto'
                    }}
                >
                    {isLoading ? (
                        <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                            textAlign: 'center',
                        }}
                        >
                            <CircularProgress size='3rem' sx={{ color: '#635bff' }} />
                            <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
                                Loading Data...
                            </Typography>
                        </Box>
                    ) : (
                        <AgGridReact
                            columnDefs={columnDefs}
                            rowData={rows}
                            defaultColDef={defaultColDef}
                            onGridReady={onGridReady}
                            onRowDoubleClicked={handleRowDoubleClick}
                            pagination={true}
                            paginationPageSize={1000}
                            paginationPageSizeSelector={[50, 100, 500, 1000, 5000]}
                            rowSelection={{ mode: 'single', enableClickSelection: false }}
                            suppressSelectionColumn={true}
                            onCellClicked={handleCellClicked}
                            getRowStyle={getRowStyle}
                            enableCellTextSelection={true}
                            ensureDomOrder={true}
                            loading={isLoading}
                            rowHeight={28}
                            headerHeight={28}
                            floatingFiltersHeight={28}
                            overlayLoadingTemplate={
                                '<span class="ag-overlay-loading-center">Loading data...</span>'
                            }
                            overlayNoRowsTemplate={
                                '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;">No data found</span>'
                            }
                            suppressMenuHide={true}
                            sideBar={false}
                            suppressHorizontalScroll={false}
                        />
                    )}
                </Box>

            </Paper>
            <Typography variant="body2" sx={{
                fontWeight: 'bold', position: 'absolute', bottom: '55px',
                display: { xs: 'none', sm: 'none', md: 'block' },
                marginLeft: '10px'
            }}>
                Total Records: {rows.length}
            </Typography>


        </Box>
    );
}
